import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { Home, ArrowLeft, Search, Heart, Shield, CheckCircle, Users } from 'lucide-react'

const NotFound: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <Container>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          {/* 404 Animation */}
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8, type: "spring" }}
            className="mb-8"
          >
            <div className="text-8xl md:text-9xl font-bold text-blue-600 mb-4">
              404
            </div>
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-12 h-12 text-blue-600" />
            </div>
          </motion.div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-6"
          >
            Page Not Found
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="text-xl text-theme-text-secondary mb-8 leading-relaxed"
          >
            Sorry, we couldn't find the page you're looking for. It might have been moved, deleted, or you entered the wrong URL.
          </motion.p>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Home className="w-5 h-5 mr-2" />
                Go Home
              </Button>
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-6 py-3 border border-theme-border text-theme-text-secondary rounded-lg hover:bg-theme-surface transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </button>
          </motion.div>

          {/* Helpful Links */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-12 pt-8 border-t border-theme-border"
          >
            <h3 className="text-lg font-semibold text-theme-text-primary mb-4">
              Helpful Links
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Link
                to="/"
                className="flex items-center text-theme-text-secondary hover:text-theme-primary transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-theme-surface flex items-center justify-center mr-3">
                  <Heart className="w-4 h-4" />
                </div>
                Home Page
              </Link>
              <Link
                to="/features"
                className="flex items-center text-theme-text-secondary hover:text-theme-primary transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-theme-surface flex items-center justify-center mr-3">
                  <Shield className="w-4 h-4" />
                </div>
                Features
              </Link>
              <Link
                to="/pricing"
                className="flex items-center text-theme-text-secondary hover:text-theme-primary transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-theme-surface flex items-center justify-center mr-3">
                  <CheckCircle className="w-4 h-4" />
                </div>
                Pricing
              </Link>
              <Link
                to="/about"
                className="flex items-center text-theme-text-secondary hover:text-theme-primary transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-theme-surface flex items-center justify-center mr-3">
                  <Users className="w-4 h-4" />
                </div>
                About Us
              </Link>
            </div>
          </motion.div>

          {/* Search Suggestion */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="mt-8 p-6 bg-blue-50 rounded-lg"
          >
            <h4 className="font-semibold text-theme-text-primary mb-2">
              Need Help?
            </h4>
            <p className="text-theme-text-secondary text-sm mb-4">
              Try searching for what you need or contact our support team for assistance.
            </p>
            <Link to="/contact">
              <Button variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                Contact Support
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  )
}

export default NotFound